import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiLoader4Line, RiShieldKeyholeLine } from '@remixicon/react';
import { supabase } from '@/lib/supabase';

/**
 * One-time MFA step-up gate for the Account Settings surface.
 *
 * Any write on `/account/settings` (org edit, branding, contract, team
 * member management, personal phone / password / two-factor, Reset MFA)
 * runs its handler through `gate()` first. The gate:
 *
 *   1. Prompts the user for their own 6-digit TOTP the FIRST time on
 *      this page mount, via a portal modal that stacks above drawers.
 *   2. Caches the verified state for the lifetime of the provider —
 *      subsequent gate() calls in the same visit resolve immediately.
 *   3. Resets on provider unmount, so navigating away and back re-arms
 *      it exactly as the spec asks.
 *   4. Skips the modal silently for users with no verified TOTP factor
 *      (internal `@gotrustjet.com` accounts that bypass MFA site-wide,
 *      matching SignIn / ProtectedRoute / ResetMfaButton conventions).
 *   5. Never fires on reads — only on the write handlers that opt in.
 *
 * Usage:
 *
 *   // Wrap the page subtree once:
 *   <MfaStepUpGateProvider>
 *     <AccountSettingsContent />
 *   </MfaStepUpGateProvider>
 *
 *   // In any write handler:
 *   const { gate } = useMfaStepUpGate();
 *   const handleSave = async () => {
 *     if (!(await gate())) return; // user cancelled — abort silently
 *     // …existing PATCH / POST body…
 *   };
 */

const MfaStepUpGateContext = createContext({
  // Fallback: no provider up-tree → gate resolves so nothing breaks if a
  // component is rendered outside the Account Settings page.
  gate: async () => {},
});

export const useMfaStepUpGate = () => useContext(MfaStepUpGateContext);

/**
 * Error thrown by `gate()` when the user cancels / closes the step-up
 * modal. Consumers add `if (isMfaCancelled(err)) return;` at the top of
 * their catch block to bail silently instead of firing a "success" or
 * "failed" toast (both are misleading — the write never started).
 */
export class MfaCancelledError extends Error {
  constructor() {
    super('MFA verification cancelled');
    this.name = 'MfaCancelledError';
  }
}

export const isMfaCancelled = (err) => err?.name === 'MfaCancelledError';

// Sentinel for "listFactors ran, user has no verified factor". Kept
// distinct from `null` (not yet probed) so the modal can distinguish
// "still loading" from "no factor, pass through".
const NO_FACTOR = '';

export const MfaStepUpGateProvider = ({ children }) => {
  // Three-way factor state:
  //   null      — probe still pending on mount
  //   ''        — probe done, user has no verified TOTP factor
  //   '<uuid>'  — factor id of the user's verified TOTP
  const [factorId, setFactorId] = useState(null);
  // Verified once per provider lifetime. Cleared automatically on
  // unmount because the whole state tree is torn down.
  const [verified, setVerified] = useState(false);
  // Pending gate() promise resolver — set when a handler calls gate()
  // and we need to open the modal. Cleared once the modal resolves.
  const pendingResolveRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);

  // One-shot factor probe on mount. Errors treated as "no factor" so
  // the gate degrades open rather than dead-locking writes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error) throw error;
        const found = (data?.totp || []).find((f) => f.status === 'verified');
        if (!cancelled) setFactorId(found?.id || NO_FACTOR);
      } catch (err) {
        console.warn('MfaStepUpGate: listFactors failed — passing writes through', err);
        if (!cancelled) setFactorId(NO_FACTOR);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const gate = useCallback(() => {
    // No factor → silent pass-through. Same convention as ResetMfaButton's
    // fallback for the @gotrustjet bypass.
    if (factorId === NO_FACTOR) return Promise.resolve();
    // Already verified this page-visit → free pass.
    if (verified) return Promise.resolve();
    // Probe still in flight — the modal below shows a spinner while
    // factorId is null. Open the modal now; it'll enable Verify once
    // the probe finishes.
    //
    // On verify success we resolve; on cancel/close we reject with
    // MfaCancelledError so the caller's outer try/catch can bail
    // silently. Rejecting (not resolving false) means downstream write
    // code never runs on cancel — a `undefined` return would otherwise
    // be interpreted as "success" by callers that toast after the
    // await.
    return new Promise((resolve, reject) => {
      pendingResolveRef.current = { resolve, reject };
      setModalOpen(true);
    });
  }, [factorId, verified]);

  const settleAndClose = (ok) => {
    const pending = pendingResolveRef.current;
    pendingResolveRef.current = null;
    setModalOpen(false);
    if (!pending) return;
    if (ok) pending.resolve();
    else pending.reject(new MfaCancelledError());
  };

  const handleSuccess = () => {
    setVerified(true);
    settleAndClose(true);
  };

  const handleCancel = () => {
    settleAndClose(false);
  };

  return (
    <MfaStepUpGateContext.Provider value={{ gate }}>
      {children}
      {modalOpen && (
        <MfaStepUpModal
          factorId={factorId}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      )}
    </MfaStepUpGateContext.Provider>
  );
};

/**
 * Step-up modal — 6-digit code input backed by supabase.auth.mfa
 * challenge / verify. Same visual language as ResetMfaButton's modal
 * (amber theme, z-[10000] portal) so admins recognize the gate
 * pattern across surfaces.
 */
const MfaStepUpModal = ({ factorId, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [saving, setSaving] = useState(false);

  const factorReady = !!factorId; // null still means "probing"
  const codeValid = /^\d{6}$/.test(code);
  const canSubmit = factorReady && codeValid && !saving;

  const handleVerify = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setCodeError(null);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (challengeError || !challengeData?.id) {
        throw new Error(challengeError?.message || 'Failed to create MFA challenge');
      }
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      if (verifyError) {
        // Wrong / expired code — clear the input, let the admin retry.
        setCodeError(verifyError.message || 'Invalid code. Please try again.');
        setCode('');
        setSaving(false);
        return;
      }
      onSuccess();
    } catch (err) {
      setCodeError(err?.message || 'Failed to verify code');
      setSaving(false);
    }
  };

  const closeIfIdle = () => {
    if (saving) return;
    onCancel();
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeIfIdle}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-amber-700 flex items-center gap-2">
            <RiShieldKeyholeLine className="w-5 h-5" />
            Verify it's you
          </h2>
          <button
            type="button"
            onClick={closeIfIdle}
            disabled={saving}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            aria-label="Cancel"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            For your security, enter the 6-digit code from your authenticator app
            to make changes on this page. You won't be asked again until you
            leave and come back.
          </p>
          {!factorReady && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <RiLoader4Line className="w-4 h-4 animate-spin" />
              Preparing verification…
            </div>
          )}
          {factorReady && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Authentication code
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  if (codeError) setCodeError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit) {
                    e.preventDefault();
                    handleVerify();
                  }
                }}
                placeholder="123456"
                className={`w-full px-4 py-2.5 border rounded-lg text-sm tracking-widest text-center font-mono focus:outline-none focus:ring-2 ${
                  codeError
                    ? 'border-red-300 focus:ring-red-200'
                    : 'border-gray-200 focus:ring-primary-200 focus:border-primary-300'
                }`}
                disabled={saving}
                autoFocus
              />
              {codeError && (
                <p className="text-xs text-red-600 mt-1.5">{codeError}</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={closeIfIdle}
              disabled={saving}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleVerify}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              {saving ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MfaStepUpGateProvider;
